import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';
import Container from '@/components/Container';
import DefaultContentTemplate from '@/components/DefaultContentTemplate';
import DefaultContentTemplateMedia from '@/components/DefaultContentTemplate/Media';
import TitleWithActions from '@/components/TitleWithActions';
import { BackButton } from '@/components/ui/back-button';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';
import {
  fetchKnowledgeBasePage,
  fetchRelatedContent,
  fetchKnowledgeBase,
} from '@/services/knowledge-base/knowledge-base-item.service';
import {
  ComponentCommonMedia,
  Enum_Like_Type,
  GENERATED,
  KnowledgeBase,
  KnowledgeBasePage as KnowledgeBasePageType,
  UploadFile,
} from '@edwin-group/people-graphql';
import MediaDuration from '@/components/MediaDuration';
import { formatPublicationDate, formatDurationFromSeconds } from '@/utils/date';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Divider from '@/components/Divider';
import KnowledgeBaseVerticalCard from '@/components/KnowledgeBaseVerticalCard';

export default function KnowledgeBasePage() {
  const [knowledgebase, setKnowledgeBase] = useState<KnowledgeBase | null>(null);
  const [knowledgeBasePage, setKnowledgeBasePage] = useState<KnowledgeBasePageType | null>();
  const [relatedContent, setRelatedContent] = useState<KnowledgeBase[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const { slug } = useParams();

  useEffect(() => {
    (async () => {
      if (!slug) return;
      setLoading(true);
      const [knowledgebaseResult, relatedContentResult, page] = await Promise.all([
        fetchKnowledgeBase(slug),
        fetchRelatedContent(slug),
        fetchKnowledgeBasePage(),
      ]);

      const knowledgebaseData = knowledgebaseResult?.knowledgebase;
      setKnowledgeBasePage(page.page as unknown as KnowledgeBasePageType);
      setKnowledgeBase(knowledgebaseData as unknown as KnowledgeBase);
      setRelatedContent(relatedContentResult.knowledgebases || []);
      setLoading(false);
      trackGAEvent('knowledge_base_opened', {
        knowledge_base_id: knowledgebaseData?.documentId ?? '',
        knowledge_base_name: knowledgebaseData?.attributes?.title ?? '',
        knowledge_base_type: knowledgebaseData?.attributes?.knowledgeBaseType?.data?.attributes?.type ?? '',
      });

      if (knowledgebaseData) {
        // const topic = knowledgebaseData.attributes?.themes?.data[0]?.attributes?.topic?.data?.attributes;
        // const topicId = knowledgebaseData.attributes?.themes?.data[0]?.attributes?.topic?.data?.id;
        // const themes = knowledgebaseData.attributes?.themes?.data?.map((theme) => `${theme.attributes?.theme}`).join(', ');
        // const themeId = knowledgebaseData.attributes?.themes?.data?.map((theme) => `${theme.id}`).join(', ');
        // trackKnowledgeBase({
        //   topic_id: topicId || '',
        //   topic_name: topic?.topic || '',
        //   theme_id: themeId || '',
        //   theme_name: themes || '',
        // });
      }
    })();
  }, [slug]);

  //   useEffect(() => {
  //     return () => {
  //       clearKnowledgeBaseDataLayer();
  //     };
  //   }, []);

  const publicationDateFormatted = useMemo(
    () =>
      formatPublicationDate(
        (knowledgebase?.publicationDate ?? knowledgebase?.attributes?.publicationDate) as string | null | undefined,
      ),
    [knowledgebase?.publicationDate, knowledgebase?.attributes?.publicationDate],
  );

  const duration = useMemo(() => {
    const raw = knowledgebase?.attributes?.duration;
    if (raw == null || raw === '') return null;
    const seconds = Number(raw);
    return Number.isFinite(seconds) ? formatDurationFromSeconds(seconds) : raw;
  }, [knowledgebase?.attributes?.duration]);

  const contentWithoutRelated = useMemo(
    () =>
      knowledgebase?.attributes?.DefaultContentTemplate?.filter(
        (c) => c?.__typename !== 'ComponentKnowledgeBaseKnowledgeBaseRelated',
      ) ?? [],
    [knowledgebase?.attributes?.DefaultContentTemplate],
  );

  const renderKnowledgeBaseHeader = () => {
    return (
      <div className="mt-[64px]">
        <Container>
          <div className="col-span-12 lg:col-start-3 lg:col-end-11">
            <BackButton to="/knowledge-base" />
          </div>
        </Container>
        <Container>
          <div className="col-span-12 lg:col-start-3 lg:col-end-11">
            {knowledgebase?.attributes?.cover.data?.attributes?.url && (
              <DefaultContentTemplateMedia
                className="highlight-image-border !mb-[45px] mt-sh-30"
                component={
                  {
                    id: '1',
                    media: {
                      data: {
                        attributes: {
                          url: knowledgebase?.attributes?.cover.data?.attributes?.url || '',
                          alternativeText: knowledgebase?.attributes?.cover.data?.attributes?.alternativeText || '',
                        } as UploadFile,
                      },
                    },
                  } as ComponentCommonMedia
                }
              />
            )}

            {knowledgebase?.documentId ? (
              <TitleWithActions
                title={knowledgebase!.attributes!.title}
                entity={knowledgebase}
                category={GENERATED.Enum_Bookmark_Category.KnowledgeBase}
                type={Enum_Like_Type.KnowledgeBase}
                knowledgeBaseTypeId={knowledgebase?.attributes?.knowledgeBaseType?.data?.id}
                entityId={knowledgebase!.documentId}
              />
            ) : null}
            {(publicationDateFormatted || duration) && (
              <div className="mb-[30px] mt-[8px] flex flex-col gap-2 font-haffer text-sh-12-18 text-people-gray-400">
                {publicationDateFormatted && <span>{publicationDateFormatted}</span>}
                {duration && <MediaDuration duration={duration} />}
              </div>
            )}
          </div>
        </Container>
      </div>
    );
  };

  return (
    <>
      {!loading && renderKnowledgeBaseHeader()}
      <Container className="min-h-40">
        <div className="col-span-12 lg:col-start-3 lg:col-end-11">
          <DefaultContentTemplate
            loading={loading}
            content={contentWithoutRelated}
            downloadableContentTitle={knowledgeBasePage?.contentTemplateLabels?.downloadableContentLabel || undefined}
          />
        </div>
        {relatedContent && relatedContent.length > 0 && (
          <AnimatedFadeInComponent className="col-span-12 mt-sh-30">
            <Divider />
            <h2 className="mb-sh-30 text-sh-24 font-bold">Related content</h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {relatedContent.map((item) => (
                <KnowledgeBaseVerticalCard
                  key={item?.documentId ?? item?.attributes?.title}
                  title={item.attributes?.title || ''}
                  description={item.attributes?.briefDescription || ''}
                  thumbnail={item.attributes?.cover.data?.attributes as UploadFile}
                  url={`/knowledge-base/${item.attributes?.slug}`}
                  watchLabel="Read now"
                />
              ))}
            </div>
          </AnimatedFadeInComponent>
        )}
      </Container>
    </>
  );
}
